import { HttpService } from 'core/modules/network-service';
import { get, merge } from 'lodash';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems,  } from 'resolvers/getVisibleEntities';
import { resolveEntityCellData } from  'resolvers/resolveEntityCellData';
import { REDUCER } from 'constants/entity-names';
import { getStore } from 'store';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberOrderDetailSummary defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberOrderDetailSummaryService } from 'services/memberOrderDetailSummary';
 *
 * // create service instance and send/cancel request
 * const memberOrderDetailSummaryService = new MemberOrderDetailSummaryService();
 * const result = await memberOrderDetailSummaryService.fetchData( '<user-id>' ); // fetch data
 * memberOrderDetailSummaryService.cancel(); // cancel an active request
 */
export class MemberOrderDetailSummaryService extends HttpService {

    // make request and fetch data
    async fetchComponentData( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        // get ongoing callType info from the redux store
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        // caller Type
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        callerType
                    },
                } ),
            } );

            const component = get( response, 'body.component', {} );
            const visibility = get( component, 'visibility', {} );
            const primarySection = get( component, 'structure.primarySection', {} );
            const secondarySection = get( component, 'structure.secondarySection', {} );
            const widgetConfig =  get( component, 'structure.widgetConfig', {} );
            const markResolved = get( component, 'structure.markResolved', {} );
            const cancelOrder = get( component, 'structure.cancelOrder', {} );
            const navActions = get( response, 'body.component.structure.navActions', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    orderData: get( response, 'body.component.data.state', {} ),
                    primarySection: resolveEntityCellData( { items: getVisibleItems( primarySection, visibility ), component } ),
                    secondarySection: resolveEntityCellData( { items: getVisibleItems( secondarySection, visibility ), component } ),
                    navActions: navActions,
                    visibility,
                    widgetConfig,
                    markResolved,
                    cancelOrder
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

     // make request and fetch data
     async initiateEditOrder( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // make request and fetch data
    async initiateReorder( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // make request for controlled drug info and fetches data
    async fetchControlledDrugReOrderInfo( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const visibility = get( response, 'body.component.data.visibility', [] );
            const columns = get( response, 'body.component.structure.primaryColumns', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    ...get( response, 'body.component.data', [] ),
                    ...get( response, 'body.component.structure', [] ),
                    primaryColumns: getVisibleItems( columns, visibility )
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }
    
    // make request and fetch data
    async initiateMarkResolved( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    resData: {
                        description: get( response, 'body.component.data.description', {} ),
                    }
                };
            }

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // make request and fetch data
    async cancelOrder( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } )
            } );

            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    resData: get( response, 'body.component.data', {} )
                };
            }

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async initNotes( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const actions = get( response, 'body.component.structure.actions', [] );
            const formActions = get( response, 'body.component.structure.formActions', [] );
            const title = get( response, 'body.component.structure.title', '' );
            const displayDateText = get( response, 'body.component.structure.displayDateText', '' );
            const fields = get( response, 'body.component.structure.fields', '' );
            const items = get( response, 'body.component.data.state.items', '' );
            const options = get( response, 'body.component.data.options', {} );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: { items, actions, title, displayDateText, fields, formActions, options },
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }
    
    // fetch live order status from ePost
    async fetchLiveOrderStatus( { data } ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: '/member/order/fetch-live-order-status',
                data: merge( {}, data, {
                    context: {
                        callId,
                        callType,
                        callerType,
                    },
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body', {} ),
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                // HttpService throws HttpServiceError with { status: <httpCode> } on non-2xx responses
                httpStatus: get( errResponse, 'status', 0 ),
                data: null,
            };
        }
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }
}
